import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../lib/auth";

// Passwordless magic-link login. The student enters their email; Supabase Auth
// emails a one-time link that redirects back to /auth/callback, where the
// session is finalized. No passwords, per the build plan (BUY, don't build).
//
// NOTE: actual email DELIVERY depends on Supabase SMTP / Resend being
// configured (deferred). Until then the request "succeeds" (Supabase accepts
// it) but no email arrives in some environments — see docs.
export default function Login() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">(
    "idle"
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { session, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from =
    (location.state as { from?: { pathname?: string } } | null)?.from
      ?.pathname ?? "/app";

  // If already logged in, skip the login page.
  useEffect(() => {
    if (!loading && session) {
      navigate(from, { replace: true });
    }
  }, [loading, session, from, navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg(null);

    const redirectTo = `${window.location.origin}/auth/callback`;
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: {
        emailRedirectTo: redirectTo,
        // Don't auto-create accounts from the login form during the build:
        // students are provisioned by the Stripe webhook. Flip to true if you
        // want open self-signup. Left true here so seeding/testing is easy.
        shouldCreateUser: true,
      },
    });

    if (error) {
      setStatus("error");
      setErrorMsg(error.message);
      return;
    }
    setStatus("sent");
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
        <p className="text-sm font-semibold uppercase tracking-wide text-txcfi-accent">
          TXCFI
        </p>
        <h1 className="mt-1 text-2xl font-bold text-txcfi-ink">
          Sign in to 210 Ground School
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          We&apos;ll email you a secure sign-in link. No password required.
        </p>

        {status === "sent" ? (
          <div className="mt-6 rounded-md bg-green-50 p-4 text-sm text-green-800">
            Check <span className="font-semibold">{email}</span> for your
            sign-in link. You can close this tab once you open it.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <label className="block text-sm font-medium text-slate-700">
              Email address
              <input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 outline-none focus:border-txcfi-accent focus:ring-1 focus:ring-txcfi-accent"
              />
            </label>
            <button
              type="submit"
              disabled={status === "sending"}
              className="w-full rounded-md bg-txcfi-accent px-4 py-2.5 font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === "sending" ? "Sending link…" : "Email me a sign-in link"}
            </button>
            {status === "error" && errorMsg && (
              <p className="text-sm text-red-600">{errorMsg}</p>
            )}
          </form>
        )}

        <p className="mt-6 text-xs text-slate-400">
          Trouble signing in? Email Scott Hutchinson, CFII.
        </p>
      </div>
    </main>
  );
}
